import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Row as ReactTableRow,
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { Tooltip } from 'react-tooltip';
import { cn } from '@Utils';

import ColumnPicker from '../ColumnPicker/columnPicker';
import IndeterminateCheckbox from '../IndeterminateCheckbox/indeterminateCheckbox';

export interface Column {
  Header?: string;
  accessor: string;
  Cell?: ({ row }) => void;
  width?: string;
}

export interface Row {
  values: object;
  depth: number;
  canExpand?: boolean;
  isExpanded?: boolean;
}

export interface TableFilter {
  column: string;
  value?: any;
}

interface TableProps {
  columns: Column[];
  data: object[];
  pagination?: boolean;
  sortable?: boolean;
  footer?: boolean;
  columnPicker?: boolean;
  selectable?: boolean;
  filters?: TableFilter[];
  pageSize: number;
  getFilteredRows?(rows: ReactTableRow<object>[]);
  getRowClassName?(row: any): string;
  getRowProps?(row: any): any;
  tooltipId?: string;
  /** Merged into every header cell, so a page can restyle the header without forking the table. */
  headerCellClassName?: string;
  /** Merged into every body cell, before the column's own `width` classes so those can still override it. */
  cellClassName?: string;
}

const Table = (props: TableProps) => {
  const {
    columns,
    data,
    pagination,
    sortable,
    footer,
    columnPicker,
    selectable,
    filters,
    getFilteredRows,
    pageSize,
    getRowClassName,
    getRowProps,
    tooltipId,
    headerCellClassName,
    cellClassName,
  } = props;

  const { t } = useTranslation();

  const {
    footerGroups,
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageCount,
    nextPage,
    previousPage,
    allColumns,
    getToggleHideAllColumnsProps,
    state: { pageIndex },
    setFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (selectable) {
        hooks.visibleColumns.push((visibleColumn) => [
          {
            id: 'selection',
            Header: ({ getToggleAllPageRowsSelectedProps }) => (
              <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
            ),
            Cell: ({ row }) => <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />,
          },
          ...visibleColumn,
        ]);
      }
    },
  );

  if (getFilteredRows) {
    getFilteredRows(rows);
  }

  useEffect(() => {
    filters?.forEach((filter) => {
      setFilter(filter.column, filter.value);
    });
  }, [filters]);

  const visibleRows = pagination ? page : rows;

  return (
    <div className="w-full overflow-x-auto">
      {columnPicker && (
        <div className="flex justify-end">
          <ColumnPicker allColumns={allColumns} getToggleHideAllColumnsProps={getToggleHideAllColumnsProps} />
        </div>
      )}
      <table className="w-full border-collapse" {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
              {headerGroup.headers.map((column) => {
                const sortByToggleProps = sortable ? column.getSortByToggleProps() : {};
                return (
                  // eslint-disable-next-line react/jsx-key
                  <th
                    className={cn(
                      'border-y border-gray-200 bg-gray-100 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600',
                      headerCellClassName,
                    )}
                    {...column.getHeaderProps(sortByToggleProps)}>
                    {column.render('Header')}
                    {column.isSorted && <span>{column.isSortedDesc ? '  ↓' : '  ↑'}</span>}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {visibleRows.map((row, rowIdx) => {
            prepareRow(row);
            const rowClassName = getRowClassName ? getRowClassName(row) : '';
            const customRowProps = getRowProps ? getRowProps(row) : {};
            const isLast = rowIdx === visibleRows.length - 1;
            return (
              // eslint-disable-next-line react/jsx-key
              <tr
                {...row.getRowProps()}
                className={cn('transition-colors hover:bg-gray-50', rowClassName)}
                {...customRowProps}>
                {row.cells.map((cell) => (
                  // eslint-disable-next-line react/jsx-key
                  <td
                    className={cn(
                      'break-words px-6 py-4 text-sm text-gray-700',
                      !isLast && 'border-b border-gray-200',
                      cellClassName,
                      cell?.column?.width,
                    )}
                    {...cell.getCellProps()}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>

        {footer && (
          <tfoot>
            {footerGroups.map((group) => (
              // Key is already present in getFooterGroupProps but eslint is not able to detect it
              // eslint-disable-next-line react/jsx-key
              <tr {...group.getFooterGroupProps()}>
                {group.headers.map((column) => (
                  // Key is already present in getFooterGroupProps but eslint is not able to detect it
                  // eslint-disable-next-line react/jsx-key
                  <td {...column.getFooterProps()}>{column.render('Footer')}</td>
                ))}
              </tr>
            ))}
          </tfoot>
        )}
      </table>

      {pagination && (
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
          <span className="text-sm text-gray-700">
            {t('Global.Page')} {pageIndex + 1} {t('Global.Of')} {pageCount || 1}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className={cn(
                'rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold transition-colors',
                pageIndex === 0 ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-gray-700 hover:bg-gray-50',
              )}
              onClick={() => previousPage()}
              disabled={pageIndex === 0}>
              {t('Global.Previous')}
            </button>
            <button
              type="button"
              className={cn(
                'rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold transition-colors',
                pageIndex !== pageCount - 1 && pageCount
                  ? 'cursor-pointer text-gray-700 hover:bg-gray-50'
                  : 'cursor-not-allowed text-gray-400',
              )}
              onClick={() => nextPage()}
              disabled={!(pageIndex !== pageCount - 1 && pageCount)}>
              {t('Global.Next')}
            </button>
          </div>
        </div>
      )}

      {tooltipId && <Tooltip id={tooltipId} />}
    </div>
  );
};

Table.defaultProps = {
  pagination: false,
  sortable: true,
  footer: false,
  columnPicker: false,
  selectable: false,
  filters: [],
  getFilteredRows: undefined,
};

export default Table;
export type { ReactTableRow };
